import type { ConnectedAccountNanoId } from '@composio/types'
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { getConnectedAccountsByNanoid } from '~/generated/client'
import {
  useGetTriggerData,
  useGetTriggerInstanceData,
} from '~/hooks/query/triggers'
import { useRouterParsed } from '~/hooks/utils/useRouterParse'
import { useGetConnectorInfo } from '~/pages/connector/[connectorId]'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { TabBarStrip } from '~/ui/components/custom/TabBarStrip'
import { RightSideContent } from '~/ui/containers/base/base'
import { LoadingState } from '~/ui/containers/base/emptyStates'
import { ActionRenderer } from '~/ui/screens/actions'
import { If } from '~/utils/reactComponent'
import { useToolkitInfo } from '../appScreen/appScreen'
import { DeleteConnectionButtonV2 } from '../connectedAccountList'
import { TriggerRenderer } from '../triggers'
import { TriggerInstanceRenderer } from '../triggers/triggerPage'
import { ConnectionInfoBlock } from './ConnectionPopOver'
import { McpTabContent } from './McpTabContent'

export const useConnectionInfo = (
  connectedAccountId?: ConnectedAccountNanoId,
) => {
  const { data, isSuccess, isError, isLoading, refetch } = useQuery(
    ['connection', connectedAccountId],

    () => getConnectedAccountsByNanoid({ nanoid: connectedAccountId! }),
    {
      enabled: !!connectedAccountId,
      staleTime: 1000 * 60 * 5,
      refetchOnMount: 'always',
    },
  )

  return {
    data,
    isSuccess,
    isError,
    isLoading,
    refetch,
  }
}

const ConnectionSectionSchema = z.enum([
  'connection info',
  'actions',
  'triggers',
  'trigger instances',
  'MCP',
])

type ConnectionSections = z.infer<typeof ConnectionSectionSchema>

export default function ConnectionScreen() {
  const { authConfigId, connectedAccountId, router } = useRouterParsed()
  const { data: connectorInfo } = useGetConnectorInfo(authConfigId)
  const { triggerData, isLoading: isTriggerLoading } = useGetTriggerData(
    connectorInfo ? connectorInfo.toolkit.slug : '',
  )
  const {
    triggerData: triggerInstanceData,
    isLoading: isTriggerInstanceLoading,
  } = useGetTriggerInstanceData()

  const { data: connectionInfo, isSuccess } =
    useConnectionInfo(connectedAccountId)


  const [sections, setSections] = useState<ConnectionSections[]>([
    'connection info',
    'actions',
    'triggers',
    'trigger instances',
    'MCP',
  ])

  useEffect(() => {
    const areTriggersAvailable =
      triggerData?.items && triggerData.items.length > 0
    if (!isTriggerLoading && !areTriggersAvailable) {
      setSections((prev) => prev.filter((section) => section !== 'triggers'))
    }

    if (!isTriggerInstanceLoading && !triggerInstanceData?.items?.length) {
      setSections((prev) =>
        prev.filter((section) => section !== 'trigger instances'),
      )
    }
  }, [
    isTriggerLoading,
    isTriggerInstanceLoading,
    triggerData,
    triggerInstanceData,
  ])

  const [selectedSection, setSelectedSection] = useState(sections[0])
  const hasTabSection = useRef(false)
  const { toolkitInfo: appInfo } = useToolkitInfo(
    connectionInfo?.toolkit?.slug!,
  )

  useEffect(() => {
    if (router.isReady && !hasTabSection.current) {
      const tab = ConnectionSectionSchema.safeParse(router.query.tab)

      if (tab.success) {
        setSelectedSection(tab.data)
        hasTabSection.current = true
      }
    }
  }, [router])

  const isLoading = !isSuccess || isTriggerInstanceLoading || isTriggerLoading

  return (
    <>
      <RightSideContent
        headerLinks={[
          { link: '/connections', text: 'Connected accounts', icon: null },
          { link: '#', text: connectedAccountId?.toString() + '', icon: null },
        ]}
      >
        <div className="h-full text-black-400">
          {isLoading ? (
            <LoadingState
              height={window?.innerHeight - 200}
              text="Fetching connection..."
            />
          ) : (
            <>
              <div className="relative mt-2">
                <TabBarStrip
                  sections={sections || []}
                  selectedSection={selectedSection}
                  setSelectedSection={setSelectedSection}
                />
                <div
                  className="absolute right-[0]"
                  style={{
                    top: '4px',
                  }}
                >
                  <DeleteConnectionButtonV2
                    connectionId={connectedAccountId!}
                  />
                </div>
              </div>
              <If condition={selectedSection === 'connection info'}>
                <div className="h-full px-4 pt-2">
                  <ErrorBoundary
                    fallback={
                      <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                    }
                  >
                    <ConnectionInfoBlock
                      connectionId={connectedAccountId!}
                      connectionInfo={connectionInfo}
                    />
                  </ErrorBoundary>
                </div>
              </If>
              <If condition={selectedSection === 'actions'}>
                <div className="h-full px-4 pt-0">
                  <ErrorBoundary
                    fallback={
                      <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                    }
                  >
                    <ActionRenderer />
                  </ErrorBoundary>
                </div>
              </If>
              <If condition={selectedSection === 'triggers'}>
                <div className="h-full px-4 pt-2">
                  <ErrorBoundary
                    fallback={
                      <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                    }
                  >
                    <TriggerRenderer />
                  </ErrorBoundary>
                </div>
              </If>
              <If condition={selectedSection === 'trigger instances'}>
                <div className="h-full px-4 pt-2">
                  <ErrorBoundary
                    fallback={
                      <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                    }
                  >
                    <TriggerInstanceRenderer />
                  </ErrorBoundary>
                </div>
              </If>
              <If condition={selectedSection === 'MCP'}>
                <div className="h-full px-4 pt-2">
                  <ErrorBoundary
                    fallback={
                      <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
                    }
                  >
                    <McpTabContent connectionInfo={connectionInfo!} />
                  </ErrorBoundary>
                </div>
              </If>
            </>
          )}
        </div>
      </RightSideContent>
    </>
  )
}
